import React, { useCallback, useMemo, useState } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  GridLegacy as Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import Spinner from '~/components/Common/Spinner';

import { useCreateTicket, useGetCandidateTickets, useUpdateTicket } from '~/hooks/apis/accommodation/useBundles';

import { BundleOffer, CreateTicketPayload, UpdateTicketPayload } from '~/services/AccommodationService';

import { ALL_CANCELLATION_POLICIES } from '../constants';

type TicketRow = NonNullable<BundleOffer['tickets']>[number];

interface Props {
  bundleId: string;
  offerId: string;
  ticket?: TicketRow;
  open: boolean;
  onClose: () => void;
}

interface OccupancyRow {
  adults: number | '';
  children: number | '';
  infants: number | '';
}

interface FormState {
  productId: string;
  name: string;
  fareType: string;
  los: number | '';
  leadDays: number | '';
  cancellationPolicies: Array<'RF' | 'NRF'>;
  includedGuests: Array<OccupancyRow>;
  perOccupant: boolean;
  perNight: boolean;
}

let nextRowKey = 0;
const allocateRowKey = () => `row-${++nextRowKey}`;

function buildInitialState(ticket: TicketRow | undefined): FormState {
  if (!ticket) {
    return {
      productId: '',
      name: '',
      fareType: '',
      los: 1,
      leadDays: 0,
      cancellationPolicies: ['RF', 'NRF'],
      includedGuests: [{ adults: 2, children: 2, infants: 1 }],
      perOccupant: false,
      perNight: false,
    };
  }
  const capacities: Array<OccupancyRow> =
    ticket.includedGuests && ticket.includedGuests.length > 0
      ? ticket.includedGuests.map((c) => ({
          adults: c.adults ?? 0,
          children: c.children ?? 0,
          infants: c.infants ?? 0,
        }))
      : [{ adults: 0, children: 0, infants: 0 }];
  return {
    productId: ticket.productId,
    name: ticket.name ?? '',
    fareType: ticket.fareType ?? '',
    los: ticket.los ?? '',
    leadDays: ticket.leadDays ?? '',
    cancellationPolicies: (ticket.cancellationPolicies ?? []) as Array<'RF' | 'NRF'>,
    includedGuests: capacities,
    perOccupant: !!ticket.perOccupant,
    perNight: !!ticket.perNight,
  };
}

function AccommodationBundleTicketDialog({ bundleId, offerId, ticket, open, onClose }: Props) {
  const isEditing = !!ticket;
  const [state, setState] = useState<FormState>(() => buildInitialState(ticket));
  const [rowKeys, setRowKeys] = useState<Array<string>>(() => state.includedGuests.map(allocateRowKey));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const createTicketMutation = useCreateTicket();
  const updateTicketMutation = useUpdateTicket();

  const updateOccupancyRow = useCallback((index: number, key: keyof OccupancyRow, value: number | '') => {
    setState((prev) => ({
      ...prev,
      includedGuests: prev.includedGuests.map((row, i) => (i === index ? { ...row, [key]: value } : row)),
    }));
  }, []);

  const addOccupancyRow = useCallback(() => {
    setState((prev) => ({
      ...prev,
      includedGuests: [...prev.includedGuests, { adults: 0, children: 0, infants: 0 }],
    }));
    setRowKeys((prev) => [...prev, allocateRowKey()]);
  }, []);

  const removeOccupancyRow = useCallback((index: number) => {
    setState((prev) => ({
      ...prev,
      includedGuests: prev.includedGuests.filter((_, i) => i !== index),
    }));
    setRowKeys((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Only load candidate tickets when creating (editing has the productId locked).
  const candidateTicketsQuery = useGetCandidateTickets({
    bundleId,
    offerId,
    enabled: !isEditing && open,
  });

  const handleChange = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setState((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setErrorMessage(null);

      const payloadBase = {
        name: state.name.trim() || undefined,
        fareType: state.fareType.trim() || undefined,
        los: typeof state.los === 'number' ? state.los : undefined,
        leadDays: typeof state.leadDays === 'number' ? state.leadDays : undefined,
        cancellationPolicies: state.cancellationPolicies,
        includedGuests: state.includedGuests.map((row) => ({
          adults: typeof row.adults === 'number' ? row.adults : 0,
          children: typeof row.children === 'number' ? row.children : 0,
          infants: typeof row.infants === 'number' ? row.infants : 0,
        })),
        perOccupant: state.perOccupant,
        perNight: state.perNight,
      };

      try {
        if (isEditing && ticket) {
          const payload: UpdateTicketPayload = payloadBase;
          await updateTicketMutation.mutateAsync({
            bundleId,
            offerId,
            ticketId: ticket.id,
            payload,
          });
        } else {
          const payload: CreateTicketPayload = {
            ...payloadBase,
            productId: state.productId.trim(),
          };
          await createTicketMutation.mutateAsync({ bundleId, offerId, payload });
        }
        onClose();
      } catch (err: unknown) {
        setErrorMessage(err instanceof Error ? err.message : 'Failed to save ticket');
      }
    },
    [isEditing, ticket, state, createTicketMutation, updateTicketMutation, bundleId, offerId, onClose],
  );

  const candidateTickets = candidateTicketsQuery.data?.tickets ?? [];
  const selectedCandidate = useMemo(
    () => candidateTickets.find((t) => t.productId === state.productId),
    [candidateTickets, state.productId],
  );

  const isSubmitting = createTicketMutation.isPending || updateTicketMutation.isPending;
  const canSubmit =
    !!state.productId && state.cancellationPolicies.length > 0 && state.includedGuests.length > 0 && !isSubmitting;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{isEditing ? 'Edit Ticket' : 'Add Ticket'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {!isEditing && (
              <Box>
                <Typography variant="subtitle2" mb={1}>
                  Pick a candidate ticket from svc-experience
                </Typography>
                {candidateTicketsQuery.isLoading ? (
                  <Spinner />
                ) : candidateTicketsQuery.error ? (
                  <Alert severity="error">
                    Failed to load candidate tickets: {candidateTicketsQuery.error.message}
                  </Alert>
                ) : candidateTickets.length === 0 ? (
                  <Alert severity="info">No candidate tickets available from svc-experience for this experience.</Alert>
                ) : (
                  <FormControl fullWidth>
                    <InputLabel>Product</InputLabel>
                    <Select
                      label="Product"
                      value={state.productId}
                      onChange={(e) => {
                        const productId = e.target.value as string;
                        const candidate = candidateTickets.find((t) => t.productId === productId);
                        setState((prev) => ({
                          ...prev,
                          productId,
                          name: candidate?.name ?? prev.name,
                          fareType: candidate?.fareType ?? prev.fareType,
                        }));
                      }}
                      required
                    >
                      {candidateTickets.map((t) => (
                        <MenuItem key={t.productId} value={t.productId}>
                          {t.name ?? t.fareType ?? t.productId}{' '}
                          {t.price != null && t.currency ? `— ${t.price} ${t.currency}` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {selectedCandidate && (
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    Selected: {selectedCandidate.name ?? selectedCandidate.fareType ?? selectedCandidate.productId}
                    {selectedCandidate.price != null && selectedCandidate.currency
                      ? ` — ${selectedCandidate.price} ${selectedCandidate.currency}`
                      : ''}
                  </Typography>
                )}
              </Box>
            )}

            {isEditing && <TextField label="Product ID" value={state.productId} disabled fullWidth />}

            <TextField
              label="Name (display label, optional)"
              value={state.name}
              onChange={(e) => handleChange('name', e.target.value)}
              fullWidth
            />

            <TextField
              label="Fare Type (optional)"
              value={state.fareType}
              onChange={(e) => handleChange('fareType', e.target.value)}
              fullWidth
            />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Min length of stay (nights)"
                  type="number"
                  value={state.los}
                  onChange={(e) => handleChange('los', e.target.value === '' ? '' : Number(e.target.value))}
                  inputProps={{ min: 0 }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Lead days"
                  type="number"
                  value={state.leadDays}
                  onChange={(e) => handleChange('leadDays', e.target.value === '' ? '' : Number(e.target.value))}
                  inputProps={{ min: 0 }}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Cancellation Policies</InputLabel>
                  <Select
                    multiple
                    label="Cancellation Policies"
                    value={state.cancellationPolicies}
                    onChange={(e) => handleChange('cancellationPolicies', e.target.value as Array<'RF' | 'NRF'>)}
                    required
                  >
                    {ALL_CANCELLATION_POLICIES.map((c) => (
                      <MenuItem key={c} value={c}>
                        {c}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" mb={1}>
                  Occupancy maximums
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  A rate must fit within at least one of these maximums to be eligible for this ticket.
                </Typography>
                <Stack spacing={2}>
                  {state.includedGuests.map((row, index) => (
                    <Box key={rowKeys[index]} border={1} borderColor="divider" borderRadius={1} p={2}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="body2">Maximum #{index + 1}</Typography>
                        <IconButton
                          onClick={() => removeOccupancyRow(index)}
                          size="small"
                          aria-label="Remove occupancy maximum"
                          disabled={state.includedGuests.length <= 1}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={4}>
                          <TextField
                            label="Max Adults"
                            type="number"
                            value={row.adults}
                            onChange={(e) =>
                              updateOccupancyRow(index, 'adults', e.target.value === '' ? '' : Number(e.target.value))
                            }
                            inputProps={{ min: 0 }}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <TextField
                            label="Max Children"
                            type="number"
                            value={row.children}
                            onChange={(e) =>
                              updateOccupancyRow(index, 'children', e.target.value === '' ? '' : Number(e.target.value))
                            }
                            inputProps={{ min: 0 }}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={4}>
                          <TextField
                            label="Max Infants"
                            type="number"
                            value={row.infants}
                            onChange={(e) =>
                              updateOccupancyRow(index, 'infants', e.target.value === '' ? '' : Number(e.target.value))
                            }
                            inputProps={{ min: 0 }}
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                </Stack>
                <Box mt={2}>
                  <Button onClick={addOccupancyRow} variant="outlined">
                    + Add maximum
                  </Button>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={state.perOccupant}
                      onChange={(e) => handleChange('perOccupant', e.target.checked)}
                    />
                  }
                  label="Per Occupant"
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={
                    <Switch checked={state.perNight} onChange={(e) => handleChange('perNight', e.target.checked)} />
                  }
                  label="Per Night"
                />
              </Grid>
            </Grid>

            {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={!canSubmit}>
            {isSubmitting ? 'Saving…' : isEditing ? 'Save Changes' : 'Create Ticket'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default AccommodationBundleTicketDialog;
